import { Injectable } from '@angular/core';
import { catchError, forkJoin, Observable, of, throwError } from 'rxjs';
import { EstrategiasReportes, ServicioReportes } from '../core/servicio-reportes';
import { ReporteRegistrado, ResultadoReporte } from '../core/reporte';
import { ReportesApiService } from '../reportes-api.services';
import { parsearBoletasHonorarios, parsearComunicaciones, parsearDatosTributarios, parsearDeudasPropiedades, parsearDevolucionesIva, parsearF22, parsearF29, parsearF50, parsearMultas, parsearRegistroComprasVentas } from './reportes-parser';
import { FormatoReportes, SII_REPORTES_DATOS } from './tipos-reporte';

function resultadoSimple<T>(data: T): ResultadoReporte<T> {
    return { data };
}

function permitirReporteInexistente<T>(solicitud: Observable<T>): Observable<T | null> {
    return solicitud.pipe(
        catchError((error: { status?: number }) => {
            if (error.status === 404) {
                return of(null);
            }

            return throwError(() => error);
        })
    );
}

@Injectable({ providedIn: 'root' })
export class SiiReportesService extends ServicioReportes<'SII', typeof SII_REPORTES_DATOS> {
    constructor(api: ReportesApiService) {
        const estrategias = {
            datosTributarios: {
                consultar: ({ idcliente }) => api.obtenerDatosTributarios(idcliente),
                transformar: (dto) => {
                    const reporte = parsearDatosTributarios(dto);

                    return {
                        tablas: reporte.tablas,
                        data: {
                            rut: reporte.rut,
                            titulo: reporte.titulo,
                            generadoEn: reporte.generadoEn,
                            ...reporte.data
                        }
                    };
                }
            },
            f22: {
                consultar: ({ idcliente, anio }) => api.obtenerF22(idcliente, anio),
                transformar: (dto) => resultadoSimple(parsearF22(dto))
            },
            f29: {
                consultar: ({ idcliente, anio }) => api.obtenerF29(idcliente, anio),
                transformar: (dto) => resultadoSimple(parsearF29(dto))
            },
            f50: {
                consultar: ({ idcliente, anio }) => api.obtenerF50(idcliente, anio),
                transformar: (dto) => resultadoSimple(parsearF50(dto))
            },
            declaracionesJuradas: {
                consultar: ({ idcliente, anio }) => api.obtenerDeclaracionesJuradas(idcliente, anio),
                transformar: resultadoSimple
            },
            multas: {
                consultar: ({ idcliente }) => api.obtenerMultas(idcliente),
                transformar: (dto) => resultadoSimple(parsearMultas(dto))
            },
            propiedades: {
                consultar: ({ idcliente }) => api.obtenerPropiedades(idcliente),
                transformar: resultadoSimple
            },
            deudasPropiedades: {
                consultar: ({ idcliente }) => api.obtenerDeudasPropiedades(idcliente),
                transformar: (dto) => resultadoSimple(parsearDeudasPropiedades(dto))
            },
            peticionesAdministrativas: {
                consultar: ({ idcliente }) => api.obtenerPeticionesAdministrativas(idcliente),
                transformar: resultadoSimple
            },
            ivaPostergado: {
                consultar: ({ idcliente, anio }) => api.obtenerIvaPostergado(idcliente, anio),
                transformar: resultadoSimple
            },
            f29Observado: {
                consultar: ({ idcliente }) => api.obtenerF29Observado(idcliente),
                transformar: resultadoSimple
            },
            expedientesTributarios: {
                consultar: ({ idcliente }) => api.obtenerExpedienteTributario(idcliente),
                transformar: resultadoSimple
            },
            agentesRetenedores: {
                consultar: ({ idcliente, anio }) => api.obtenerAgentesRetenedores(idcliente, anio),
                transformar: resultadoSimple
            },
            boletas: {
                consultar: ({ idcliente, anio }) =>
                    forkJoin({
                        recibidas: permitirReporteInexistente(api.obtenerBoletasRecibidas(idcliente, anio)),
                        emitidas: permitirReporteInexistente(api.obtenerBoletasHonorariosEmitidas(idcliente, anio)),
                        terceros: permitirReporteInexistente(api.obtenerBoletasTercerosEmitidas(idcliente, anio))
                    }),
                transformar: (dto, { anio }) => resultadoSimple(parsearBoletasHonorarios(dto, anio))
            },
            registroComprasVentas: {
                consultar: ({ idcliente, anio, mes }) => api.obtenerRegistroComprasVentas(idcliente, anio, mes),
                transformar: (dto) => resultadoSimple(parsearRegistroComprasVentas(dto))
            },
            comunicaciones: {
                consultar: ({ idcliente }) => api.obtenerComunicaciones(idcliente),
                transformar: (dto) => resultadoSimple(parsearComunicaciones(dto))
            },
            devolucionesIva: {
                consultar: ({idcliente, anio}) => api.obtenerDevolucionesIva(idcliente, anio),
                transformar: (dto) => resultadoSimple(parsearDevolucionesIva(dto))
            }
        } satisfies EstrategiasReportes<typeof SII_REPORTES_DATOS>;

        super(
            {
                codigo: 'SII',
                nombre: 'SII Reportes',
                ruta: '/sii-reportes',
                icono: 'pi pi-chart-bar',
                permiso: 'reportes.ver'
            },
            SII_REPORTES_DATOS,
            estrategias
        );
    }
}

export type ReporteSii = ReporteRegistrado<FormatoReportes>;
